import React, { useMemo } from "react";
import {
  FaTimes,
  FaMicrochip,
  FaCube,
  FaClock,
  FaCheckCircle,
  FaBan,
} from "react-icons/fa";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";
import { groupConsumptionProduct } from "../../../utils/consumptionProductGroup";

/**
 * Firma Raporu > Tüketim — satıra tıklayınca açılan CİHAZ KIRILIMI drawer'ı.
 *
 * Neden var: rapor satırı yalnızca toplamı gösteriyordu ("24 Ağustos · Çay
 * 735"). O toplamın hangi cihazdan geldiği ve cihazın o an AKTİF mi PASİF mi
 * olduğu görünmüyordu. Pasif cihazların tüketimi de artık her ekranda
 * sayıldığı için (2026-08-25 kararı) ayrımın görünür olması gerekiyor.
 *
 * Veri satırın kendisinden gelir (`_logs`) — ikinci bir istek atılmaz.
 *
 * `isActive` = logun YAZILDIĞI ANDAKİ cihaz durumu, cihazın bugünkü durumu
 * DEĞİL. Aynı cihaz bir gün aktif bir gün pasif olabildiği için kırılım
 * seri no + durum ikilisine göre yapılır.
 */
const ConsumentDeviceDrawer = ({ open, onClose, row }) => {
  const { mounted, entered, data: view } = useDrawerTransition(open, row);
  const data = view ?? row;

  const { groups, total, title } = useMemo(() => {
    const logs = Array.isArray(data?._logs) ? data._logs : [];
    const map = new Map();
    let sum = 0;

    logs.forEach((log) => {
      const serialNo = log.serialNo || "Bilinmeyen cihaz";
      // Aktiflik alanı yoksa (eski kayıt) "bilinmiyor" olarak ayrılır —
      // sessizce "aktif" saymak yanıltıcı olurdu.
      const active =
        log.isActive === true ? "aktif" : log.isActive === false ? "pasif" : "bilinmiyor";
      const key = `${serialNo}__${active}`;
      const amount = Number(log.consument) || 0;
      sum += amount;

      let g = map.get(key);
      if (!g) {
        g = { serialNo, active, total: 0, products: new Map(), count: 0 };
        map.set(key, g);
      }
      g.total += amount;
      g.count += 1;
      // Raporla AYNI gruplama: satırda "Espresso" yazıyorsa drawer'da da
      // espresso içecekleri tek başlıkta toplanır (bkz. consumptionProductGroup).
      const p = groupConsumptionProduct(log.productName);
      g.products.set(p, (g.products.get(p) || 0) + amount);
    });

    return {
      groups: Array.from(map.values())
        .map((g) => ({
          ...g,
          products: Array.from(g.products.entries()).map(([name, qty]) => ({
            name,
            qty,
          })),
        }))
        .sort((a, b) => b.total - a.total),
      total: sum,
      title: data?.date || "-",
    };
  }, [data]);

  const badge = (active) => {
    if (active === "aktif") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <FaCheckCircle className="w-3 h-3" /> Kota Almaya Açıkken
        </span>
      );
    }
    if (active === "pasif") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
          <FaBan className="w-3 h-3" /> Kota Almaya Kapalıyken
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold bg-gray-100 text-gray-500 border border-gray-200">
        Durum bilinmiyor
      </span>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[520px] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9997] flex flex-col bg-gray-50`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-100 shrink-0">
              <FaMicrochip className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-gray-800 truncate">
                Cihaz Bazında Tüketim
              </h3>
              <p className="text-sm text-gray-500 truncate">{title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors shrink-0"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
          {/* Toplam */}
          <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-gray-400 font-semibold tracking-wider">
              TOPLAM TÜKETİM
            </span>
            <span className="text-lg font-extrabold text-indigo-600 tabular-nums">
              {total.toLocaleString("tr-TR")}
            </span>
          </div>

          {groups.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl px-4 py-6 text-center">
              <p className="text-sm text-gray-400">
                Bu satır için cihaz kırılımı bulunamadı.
              </p>
            </div>
          ) : (
            groups.map((g) => (
              <div
                key={`${g.serialNo}-${g.active}`}
                className="bg-white border border-gray-100 rounded-xl px-4 py-3"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-800 font-mono truncate">
                      {g.serialNo}
                    </p>
                    <div className="mt-1">{badge(g.active)}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-base font-extrabold text-indigo-600 tabular-nums">
                      {g.total.toLocaleString("tr-TR")}
                    </p>
                    <p className="text-[11px] text-gray-400 inline-flex items-center gap-1 justify-end">
                      <FaClock className="w-2.5 h-2.5" />
                      {g.count} kayıt
                    </p>
                  </div>
                </div>

                {/* Ürün kırılımı — birden fazla ürün varsa anlamlı */}
                <div className="border-t border-gray-100 pt-2 space-y-1">
                  {g.products.map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="text-xs text-gray-500 inline-flex items-center gap-1.5 truncate">
                        <FaCube className="w-2.5 h-2.5 text-gray-300 shrink-0" />
                        {p.name}
                      </span>
                      <span className="text-xs font-semibold text-gray-700 tabular-nums shrink-0">
                        {p.qty.toLocaleString("tr-TR")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9996] transition-opacity duration-300 ease-in-out ${
            entered ? "opacity-100" : "opacity-0"
          }`}
          onClick={onClose}
        />
      )}
    </>
  );
};

export default ConsumentDeviceDrawer;
